/**
 * Shared verbatim by `parseRequest` and `getRouter`'s client placeholder, which is only safe if it equals the
 * dehydrated value by construction; mirrored copies drift into the TEC-3689 mismatches. `host` includes the port.
 */

export function portalIdFromHost(host: string): string {
	return host.split('.')[0] ?? '';
}

/** From the host, not the connection, which is plain HTTP behind the ALB; the client must match it. */
export function baseUrlFromHost(host: string): string {
	return (host.includes('localhost') ? 'http://' : 'https://') + host;
}

/**
 * Ask before swapping a first label: the apex's is the domain, and swapping it pointed portal links at a domain
 * we do not own (TEC-3910). Counted, not matched by name, because snd is a different registrable domain.
 */
export function isBaseDomainHost(host: string): boolean {
	const labels = host.replace(/\.$/, '').split('.');
	const last = labels.at(-1) ?? '';
	return last === 'localhost' || last.startsWith('localhost:') ? labels.length < 2 : labels.length < 3;
}

/** `path` is the public one (`/login`): a plain `/ngos/login` is another host, and rewrites to `/ngos/ngos/login`. */
export function ngoAreaUrl(baseUrl: string, path: string): string {
	let host: string;
	try {
		host = new URL(baseUrl).host;
	} catch {
		return path;
	}
	if (!host) return path;

	const ngoHost = isBaseDomainHost(host) ? `ngos.${host}` : ['ngos', ...host.split('.').slice(1)].join('.');
	return `${baseUrlFromHost(ngoHost)}${path}`;
}

/**
 * Case-sensitive like `rewrite.input`. Makes the path authoritative before the router is built: via a later
 * rewrite, a `/en/…` URL rendered in Accept-Language (TEC-3696).
 */
export function localeFromPathname<T extends string>(pathname: string, locales: readonly T[]): T | undefined {
	const first = pathname.split('/').find(Boolean);
	return first !== undefined && (locales as readonly string[]).includes(first) ? (first as T) : undefined;
}
